import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';

const FEATURES: { icon: React.ReactNode; title: string; text: string }[] = [
  {
    icon: <RateReviewOutlinedIcon />,
    title: 'Feedbacks',
    text: 'Avaliação estruturada do gestor ao desenvolvedor, com histórico por ciclo, relatórios e devolutiva reversa.',
  },
  {
    icon: <AccessTimeOutlinedIcon />,
    title: 'Ponto e banco de horas',
    text: 'Entrada e saída, resumo por dia, justificativas, pedidos de banco e consolidação mensal.',
  },
  {
    icon: <TrendingUpOutlinedIcon />,
    title: 'Desenvolvimento pessoal',
    text: 'Metas por competência, prazos e acompanhamento para a equipe e para o próprio colaborador.',
  },
  {
    icon: <GroupsOutlinedIcon />,
    title: 'RH e perfil comportamental',
    text: 'Menu dedicado ao RH: ponto eletrônico, perfil comportamental e competências a desenvolver.',
  },
];

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [passkeyLoading, setPasskeyLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  // Adaptive login: does this email have a passkey enrolled?
  const [hasPasskey, setHasPasskey] = useState<boolean>(false);
  const [checkingPasskey, setCheckingPasskey] = useState<boolean>(false);
  // User explicitly chose to fall back to password despite having a passkey.
  const [forcePassword, setForcePassword] = useState<boolean>(false);
  const availabilityCache = useRef<Map<string, boolean>>(new Map());
  const authContext = useContext(AuthContext);
  const { login, loginWithSession } = authContext || {};
  const navigate = useNavigate();

  const passkeySupported = browserSupportsWebAuthn();
  const validEmail = /\S+@\S+\.\S+/.test(email);
  // Passkey-only mode: account has a passkey and the user hasn't opted out.
  const passkeyMode = passkeySupported && hasPasskey && !forcePassword;

  // Debounced check for whether the typed email has a passkey.
  useEffect(() => {
    // Re-evaluate per account: dropping the manual password override.
    setForcePassword(false);
    if (!passkeySupported || !validEmail) {
      setHasPasskey(false);
      return;
    }
    const key = email.trim().toLowerCase();
    const cached = availabilityCache.current.get(key);
    if (cached !== undefined) {
      setHasPasskey(cached);
      return;
    }
    let cancelled = false;
    setCheckingPasskey(true);
    const t = window.setTimeout(async () => {
      try {
        const { data } = await api.get<{ available: boolean }>('/auth/passkey/available', {
          params: { email: key },
        });
        availabilityCache.current.set(key, data.available);
        if (!cancelled) setHasPasskey(data.available);
      } catch {
        if (!cancelled) setHasPasskey(false);
      } finally {
        if (!cancelled) setCheckingPasskey(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      setCheckingPasskey(false);
    };
  }, [email, passkeySupported, validEmail]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (login) {
        await login(email, password);
        navigate('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async (): Promise<void> => {
    setError('');
    if (!email) {
      setError('Informe seu e-mail para entrar com passkey.');
      return;
    }
    setPasskeyLoading(true);
    try {
      const { data: options } = await api.post('/auth/passkey/login/options', { email });
      const assertion = await startAuthentication({ optionsJSON: options });
      const { data } = await api.post<{ access_token: string; user: any }>(
        '/auth/passkey/login/verify',
        { email, response: assertion },
      );
      if (loginWithSession) {
        loginWithSession(data);
        navigate('/');
      }
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setError('Autenticação por passkey cancelada.');
      } else {
        setError(err?.response?.data?.message || 'Não foi possível entrar com passkey.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.1fr 460px' },
        bgcolor: 'background.default',
      }}
    >
      {/* Brand showcase — solid blue, hidden on mobile */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 4,
          p: { md: 6, lg: 8 },
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#ffffff',
                p: 0.75,
              }}
            >
              <Box
                component="img"
                src="/brand-logo.png"
                alt="Aplopes Tecnologia"
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>Feedback</Typography>
          </Box>

          <Chip
            label="Gestão de performance"
            sx={{
              bgcolor: 'rgba(255,255,255,0.16)',
              color: 'primary.contrastText',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              mb: 3,
            }}
          />
          <Typography
            component="h1"
            sx={{ fontWeight: 800, fontSize: { md: '2.6rem', lg: '3.1rem' }, lineHeight: 1.05, letterSpacing: '-0.03em', maxWidth: 560 }}
          >
            Feedback, ponto e metas no mesmo lugar.
          </Typography>
          <Typography sx={{ mt: 2, maxWidth: 520, opacity: 0.9, fontSize: '1.02rem', lineHeight: 1.6 }}>
            Avaliações gestor e desenvolvedor (incluindo reverso), ponto eletrônico e metas — cada perfil com sua visão.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {FEATURES.map((f) => (
            <Box
              key={f.title}
              sx={{
                p: 2.5,
                bgcolor: 'rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'inline-flex', '& svg': { fontSize: 22 } }}>{f.icon}</Box>
                <Typography sx={{ fontWeight: 700 }}>{f.title}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.5, opacity: 0.85 }}>{f.text}</Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={{ fontSize: '0.82rem', opacity: 0.75 }}>
          Dashboard e permissões adaptados a administrador, RH, gestor e desenvolvedor · interface responsiva.
        </Typography>
      </Box>

      {/* Auth panel */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 3, sm: 6 },
          py: { xs: 5, md: 0 },
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
          {/* Mobile brand */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '1.3rem',
              }}
            >
              F
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>Feedback</Typography>
          </Box>

          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: '0.12em' }}>
            Acesso
          </Typography>
          <Typography component="h2" sx={{ fontWeight: 800, fontSize: '1.8rem', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            Acesse sua conta
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 4 }}>
            Use o e-mail e a senha da sua organização para acessar o painel, os feedbacks e os módulos de RH.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              id="email"
              type="email"
              label="Email corporativo"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu.email@empresa.com"
              autoComplete="email"
              required
              fullWidth
              autoFocus
            />

            {/* Password field — hidden when the account uses passkey-only mode. */}
            {!passkeyMode && (
              <TextField
                id="password"
                type={showPassword ? 'text' : 'password'}
                label="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                autoComplete="current-password"
                required={!passkeyMode}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((current) => !current)}
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          edge="end"
                          sx={{ color: 'primary.main', fontSize: '0.85rem', fontWeight: 700, px: 1 }}
                        >
                          {showPassword ? 'Ocultar' : 'Mostrar'}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}

            {passkeyMode && (
              <Alert
                icon={<FingerprintOutlinedIcon fontSize="inherit" />}
                severity="success"
                sx={{ borderRadius: 0, '& .MuiAlert-message': { py: 0.25 } }}
              >
                Esta conta usa passkey. Entre com sua biometria, PIN ou chave do dispositivo.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ borderRadius: 0 }}>
                {error}
              </Alert>
            )}

            {passkeyMode ? (
              <>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => void handlePasskeyLogin()}
                  disabled={passkeyLoading}
                  startIcon={
                    passkeyLoading ? <CircularProgress size={18} color="inherit" /> : <FingerprintOutlinedIcon />
                  }
                  sx={{ height: 56, fontSize: '1rem' }}
                >
                  {passkeyLoading ? 'Verificando…' : 'Entrar com passkey'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={() => {
                      setForcePassword(true);
                      setError('');
                    }}
                    disabled={passkeyLoading}
                  >
                    Usar senha
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading || passkeyLoading || checkingPasskey}
                  sx={{ height: 56, fontSize: '1rem' }}
                >
                  {loading ? 'Entrando...' : checkingPasskey ? 'Verificando…' : 'Entrar'}
                </Button>
                {passkeySupported && hasPasskey && forcePassword && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      type="button"
                      variant="text"
                      size="small"
                      startIcon={<FingerprintOutlinedIcon />}
                      onClick={() => {
                        setForcePassword(false);
                        setError('');
                      }}
                    >
                      Voltar para passkey
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 3, alignItems: 'center', color: 'text.secondary' }}>
            <LockOutlinedIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2">Acesso seguro · sessão por usuário</Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
